import { AccountBalance, ConsistencyRules, Program } from '@monorepo/types';
import { DEFAULT_VALUES } from '../constants/objective-constants';
import {
  calculateBaseBalanceWithCredits,
  getBaseEquity,
  getMaxSnapshot,
} from './daily-limit-calculator';

/**
 * Business logic utilities for objective calculations
 */

export const getConsistencyTarget = (
  program?: Program | null,
): number | undefined => {
  return program?.config?.consistencyRules?.find(
    (rule) => rule.rule === ConsistencyRules.ProfitableDays,
  )?.value;
};

export const getCurrentInactiveDays = (
  accountBalance?: AccountBalance,
): number => {
  return (
    accountBalance?.consecutiveInactivityDays || DEFAULT_VALUES.INACTIVE_DAYS
  );
};

export const getInactivityLimit = (program?: Program | null): number => {
  return program?.config?.inactivityDays || DEFAULT_VALUES.INACTIVE_DAYS;
};

export const getCurrentProfitableDays = (
  profitableDaysData?: { profitableDays?: unknown[] } | null,
): number => {
  return (
    profitableDaysData?.profitableDays?.length || DEFAULT_VALUES.PROFITABLE_DAYS
  );
};

export const getDailyLimitValue = (
  accountBalance: AccountBalance,
  program?: Program | null,
): number => {
  const allowedLoss = (accountBalance.allowedDailyLosses || 0) * -1;
  const baseBalanceWithCredits = calculateBaseBalanceWithCredits(
    program,
    accountBalance,
  );
  const maxSnapshot = getMaxSnapshot(accountBalance, baseBalanceWithCredits);
  const baseEquity = getBaseEquity(accountBalance, baseBalanceWithCredits);
  const dailyLimitLevel = maxSnapshot - allowedLoss;

  return Math.max(0, baseEquity - dailyLimitLevel);
};

export const getDailyLimitLevel = (
  accountBalance: AccountBalance,
  program?: Program | null,
): number => {
  const allowedLoss = (accountBalance.allowedDailyLosses || 0) * -1;
  const baseBalanceWithCredits = calculateBaseBalanceWithCredits(
    program,
    accountBalance,
  );
  const maxSnapshot = getMaxSnapshot(accountBalance, baseBalanceWithCredits);
  return maxSnapshot - allowedLoss;
};

export interface DailyLimitTooltipData {
  lastDayMaxSnapshot: number;
  dailyLimitPercentage: number;
  dailyLimit: number;
}

export const getDailyLimitTooltipData = (
  accountBalance: AccountBalance,
  program: Program,
): DailyLimitTooltipData => {
  const { snapshotEquity, snapshotBalance, allowedDailyLosses } =
    accountBalance;

  const { dailyLossPercentage, dailyPausePercentage, buyingPower } =
    program.config;

  const lastDayMaxSnapshot =
    Math.max(snapshotBalance || 0, snapshotEquity || 0) || buyingPower || 0;
  const dailyLimitPercentage = dailyLossPercentage || dailyPausePercentage || 0;
  const dailyLimit = Math.round(Math.abs(allowedDailyLosses || 0) * 100) / 100;

  return {
    lastDayMaxSnapshot,
    dailyLimitPercentage,
    dailyLimit,
  };
};

export const getMaxLossValue = (program: Program): number => {
  if (!program.config.buyingPower || !program.config.maxLoss) {
    return 0;
  }
  return program.config.buyingPower - program.config.maxLoss;
};
